import { getWowheadLanguagePrefix } from '../constants/lang.js';
import { ActionID as ActionIdProto } from '../proto/common.js';
import { ResourceType } from '../proto/api.js';
import { OtherAction } from '../proto/common.js';
import { IconData } from '../proto/ui.js';
import {
	UIItem as Item,
} from '../proto/ui.js';

import { Database } from './database.js';
import { CHARACTER_LEVEL } from '../constants/mechanics.js';
import { t } from '../i18n/index.js';

// If true uses wotlkdb.com, else uses wowhead.com.
export const USE_WOTLK_DB = false;

// Uniquely identifies a specific item / spell / thing in WoW. This object is immutable.
export class ActionId {
	readonly itemId: number;
	readonly spellId: number;
	readonly otherId: OtherAction;
	readonly tag: number;

	readonly baseName: string; // The name without any tag additions.
	readonly name: string;
	readonly iconUrl: string;

	private constructor(itemId: number, spellId: number, otherId: OtherAction, tag: number, baseName: string, name: string, iconUrl: string) {
		this.itemId = itemId;
		this.spellId = spellId;
		this.otherId = otherId;
		this.tag = tag;

		switch (otherId) {
			case OtherAction.OtherActionNone:
				break;
			case OtherAction.OtherActionWait:
				baseName = t('otherActions.wait');
				iconUrl = '/wotlk/assets/icons/inv_misc_pocketwatch_01.jpg';
				break;
			case OtherAction.OtherActionManaRegen:
				name = t('otherActions.manaTick');
				iconUrl = resourceTypeToIcon[ResourceType.ResourceTypeMana];
				if (tag == 1) {
					name += ` (${t('gear.suffix.casting')})`;
				} else if (tag == 2) {
					name += ` (${t('gear.suffix.notCasting')})`;
				}
				break;
			case OtherAction.OtherActionEnergyRegen:
				baseName = t('otherActions.energyTick');
				iconUrl = resourceTypeToIcon[ResourceType.ResourceTypeEnergy];
				break;
			case OtherAction.OtherActionFocusRegen:
				baseName = t('otherActions.focusTick');
				iconUrl = resourceTypeToIcon[ResourceType.ResourceTypeFocus];
				break;
			case OtherAction.OtherActionManaGain:
				baseName = t('otherActions.manaGain');
				iconUrl = resourceTypeToIcon[ResourceType.ResourceTypeMana];
				break;
			case OtherAction.OtherActionRageGain:
				baseName = t('otherActions.rageGain');
				iconUrl = resourceTypeToIcon[ResourceType.ResourceTypeRage];
				break;
			case OtherAction.OtherActionAttack:
				name = t('otherActions.attack');
				iconUrl = '/wotlk/assets/icons/inv_sword_04.jpg';
				if (tag == 1) {
					name += ` (${t('gear.suffix.mainHand')})`;
				} else if (tag == 2) {
					name += ` (${t('gear.suffix.offHand')})`;
				}
				break;
			case OtherAction.OtherActionShoot:
				name = t('otherActions.shoot');
				iconUrl = '/wotlk/assets/icons/ability_marksmanship.jpg';
				break;
			case OtherAction.OtherActionPet:
				break;
			case OtherAction.OtherActionRefund:
				baseName = t('otherActions.refund');
				iconUrl = '/wotlk/assets/icons/inv_misc_coin_01.jpg';
				break;
			case OtherAction.OtherActionDamageTaken:
				baseName = t('otherActions.damageTaken');
				iconUrl = '/wotlk/assets/icons/inv_sword_04.jpg';
				break;
			case OtherAction.OtherActionHealingModel:
				baseName = t('otherActions.incomingHps');
				iconUrl = '/wotlk/assets/icons/spell_holy_renew.jpg';
				break;
			case OtherAction.OtherActionBloodRuneGain:
				baseName = t('otherActions.bloodRuneGain');
				iconUrl = '/wotlk/assets/icons/spell_deathknight_deathstrike.jpg';
				break;
			case OtherAction.OtherActionFrostRuneGain:
				baseName = t('otherActions.frostRuneGain');
				iconUrl = '/wotlk/assets/icons/spell_deathknight_deathstrike2.jpg';
				break;
			case OtherAction.OtherActionUnholyRuneGain:
				baseName = t('otherActions.unholyRuneGain');
				iconUrl = '/wotlk/assets/icons/spell_deathknight_empowerruneblade.jpg';
				break;
			case OtherAction.OtherActionDeathRuneGain:
				baseName = t('otherActions.deathRuneGain');
				iconUrl = '/wotlk/assets/icons/spell_deathknight_empowerruneblade.jpg';
				break;
			case OtherAction.OtherActionPotion:
				baseName = t('otherActions.potion');
				iconUrl = '/wotlk/assets/icons/inv_alchemy_elixir_04.jpg';
				break;
		}
		this.baseName = baseName;
		this.name = name || baseName;
		this.iconUrl = iconUrl;
	}

	anyId(): number {
		return this.itemId || this.spellId || this.otherId;
	}

	equals(other: ActionId): boolean {
		return this.equalsIgnoringTag(other) && this.tag == other.tag;
	}

	equalsIgnoringTag(other: ActionId): boolean {
		return (
			this.itemId == other.itemId
			&& this.spellId == other.spellId
			&& this.otherId == other.otherId);
	}

	setBackground(elem: HTMLElement) {
		if (this.iconUrl) {
			elem.style.backgroundImage = `url('${this.iconUrl}')`;
		}
	}

	static makeItemUrl(id: number): string {
		const langPrefix = getWowheadLanguagePrefix();
		if (USE_WOTLK_DB) {
			return 'https://wotlkdb.com/?item=' + id;
		} else {
			return `https://wowhead.com/wotlk/${langPrefix}item=${id}?lvl=${CHARACTER_LEVEL}`;
		}
	}
	static makeSpellUrl(id: number): string {
		const langPrefix = getWowheadLanguagePrefix();
		if (USE_WOTLK_DB) {
			return 'https://wotlkdb.com/?spell=' + id;
		} else {
			return `https://wowhead.com/wotlk/${langPrefix}spell=${id}`;
		}
	}
	static makeQuestUrl(id: number): string {
		const langPrefix = getWowheadLanguagePrefix();
		if (USE_WOTLK_DB) {
			return 'https://wotlkdb.com/?quest=' + id;
		} else {
			return `https://wowhead.com/wotlk/${langPrefix}quest=${id}`;
		}
	}
	static makeNpcUrl(id: number): string {
		const langPrefix = getWowheadLanguagePrefix();
		if (USE_WOTLK_DB) {
			return 'https://wotlkdb.com/?npc=' + id;
		} else {
			return `https://wowhead.com/wotlk/${langPrefix}npc=${id}`;
		}
	}
	static makeZoneUrl(id: number): string {
		const langPrefix = getWowheadLanguagePrefix();
		if (USE_WOTLK_DB) {
			return 'https://wotlkdb.com/?zone=' + id;
		} else {
			return `https://wowhead.com/wotlk/${langPrefix}zone=${id}`;
		}
	}

	setWowheadHref(elem: HTMLAnchorElement) {
		// Store ID in data attribute for local tooltip system
		if (this.itemId) {
			elem.dataset.itemId = this.itemId.toString();
			// Keep href for accessibility/open-in-new-tab, but use local URL
			elem.href = '#item-' + this.itemId;
		} else if (this.spellId) {
			elem.dataset.spellId = this.spellId.toString();
			elem.href = '#spell-' + this.spellId;
		}
	}

	setBackgroundAndHref(elem: HTMLAnchorElement) {
		this.setBackground(elem);
		this.setWowheadHref(elem);
	}

	async fillAndSet(elem: HTMLAnchorElement, setHref: boolean, setBackground: boolean): Promise<ActionId> {
		const filled = await this.fill();
		if (setHref) {
			filled.setWowheadHref(elem);
		}
		if (setBackground) {
			filled.setBackground(elem);
		}
		return filled;
	}

	// Returns an ActionId with the name and iconUrl fields filled.
	// playerIndex is the optional index of the player to whom this ID corresponds.
	async fill(playerIndex?: number): Promise<ActionId> {
		if (this.name || this.iconUrl) {
			return this;
		}

		if (this.otherId) {
			return this;
		}

		const tooltipData = await ActionId.getTooltipData(this);

		const baseName = tooltipData['name'];
		let name = baseName;
		switch (baseName) {
			case 'Explosive Shot':
			case '爆炸射击': // Explosive Shot in Chinese
				if (this.spellId == 60053) {
					name += ` (R${t('gear.suffix.rank')} 4)`;
				} else if (this.spellId == 60052) {
					name += ` (R${t('gear.suffix.rank')} 3)`;
				}
				break;
			case 'Explosive Trap':
			case '爆炸陷阱': // Explosive Trap in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.weaving')})`;
				}
				break;
			case 'Arcane Blast':
			case '奥术冲击': // Arcane Blast in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.noStacks')})`;
				} else if (this.tag == 2) {
					name += ` (1 ${t('gear.suffix.stack')})`;
				} else if (this.tag > 2) {
					name += ` (${this.tag - 1} ${t('gear.suffix.stack')})`;
				}
				break;
			case 'Hot Streak':
			case '热度': // Hot Streak in Chinese
				if (this.tag) name += ` (${t('gear.suffix.crits')})`;
				break;
			case 'Fireball':
			case '火球术': // Fireball in Chinese
			case 'Flamestrike':
			case '火焰冲击': // Flamestrike in Chinese
				if (this.tag == 8) {
					name += ` (${t('gear.suffix.rank')} 8)`;
				} else if (this.tag == 9) {
					name += ` (${t('gear.suffix.rank')} 9)`;
				}
				break;
			case 'Pyroblast':
			case '炎爆术': // Pyroblast in Chinese
				if (this.tag) name += ` (${t('gear.suffix.dot')})`;
				break;
			case 'Living Bomb':
			case '活动炸弹': // Living Bomb in Chinese
				if (this.spellId == 55362) name += ` (${t('gear.suffix.explosion')})`;
				break;
			case 'Evocation':
			case '奥术强化': // Evocation in Chinese
				if (this.tag == 1) {
					name += ` (1 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 2) {
					name += ` (2 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 3) {
					name += ` (3 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 4) {
					name += ` (4 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 5) {
					name += ` (5 ${t('gear.suffix.tick')})`;
				}
				break;
			case 'Mind Flay':
			case '精神鞭笞': // Mind Flay in Chinese
				if (this.tag == 1) {
					name += ` (1 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 2) {
					name += ` (2 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 3) {
					name += ` (3 ${t('gear.suffix.tick')})`;
				}
				break;
			case 'Mind Sear':
			case '精神灼烧': // Mind Sear in Chinese
				if (this.tag == 1) {
					name += ` (1 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 2) {
					name += ` (2 ${t('gear.suffix.tick')})`;
				} else if (this.tag == 3) {
					name += ` (3 ${t('gear.suffix.tick')})`;
				}
				break;
			case 'Shattering Throw':
			case '碎裂投掷': // Shattering Throw in Chinese
				if (this.tag === playerIndex) {
					name += ` (${t('gear.suffix.self')})`;
				}
				break;
			case 'Envenom':
			case '毒伤': // Envenom in Chinese
			case 'Eviscerate':
			case '剔骨': // Eviscerate in Chinese
			case 'Expose Armor':
			case '破甲': // Expose Armor in Chinese
			case 'Rupture':
			case '割裂': // Rupture in Chinese
			case 'Slice and Dice':
			case '切割': // Slice and Dice in Chinese
				if (this.tag) name += ` (${this.tag} ${t('gear.suffix.cp')})`;
				break;
			case 'Instant Poison IX':
			case '速效毒药 IX': // Instant Poison IX in Chinese
			case 'Wound Poison VII':
			case '致伤毒药 VII': // Wound Poison VII in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.deadly')})`
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.shiv')})`
				}
				break;
			case 'Fan of Knives':
			case '刀扇': // Fan of Knives in Chinese
			case 'Killing Spree':
			case '暗影之舞': // Killing Spree in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.mainHand')})`
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.offHand')})`
				}
				break;
			case 'Tricks of the Trade':
			case '嫁祸诀窍': // Tricks of the Trade in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.notSelf')})`
				}
				break;
			case 'Chain Lightning':
			case '闪电链': // Chain Lightning in Chinese
			case 'Lightning Bolt':
			case '闪电箭': // Lightning Bolt in Chinese
				if (this.tag == 6) {
					name += ` (${t('gear.suffix.lo')})`;
				} else if (this.tag) {
					name += ` (${this.tag} ${t('gear.suffix.mw')})`;
				}
				break;
			case 'Holy Shield':
			case '神圣之盾': // Holy Shield in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.proc')})`;
				}
				break;
			case 'Righteous Vengeance':
			case '正义复仇': // Righteous Vengeance in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.application')})`
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.dot')})`
				}
				break;
			case 'Holy Vengeance':
			case '神圣复仇': // Holy Vengeance in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.application')})`
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.dot')})`
				}
				break;
			// For targetted buffs, tag is the source player's raid index or -1 if none.
			case 'Bloodlust':
			case '嗜血': // Bloodlust in Chinese
			case 'Ferocious Inspiration':
			case '凶猛灵感': // Ferocious Inspiration in Chinese
			case 'Innervate':
			case '激活': // Innervate in Chinese
			case 'Focus Magic':
			case '专注魔法': // Focus Magic in Chinese
			case 'Mana Tide Totem':
			case '法力之潮图腾': // Mana Tide Totem in Chinese
			case 'Power Infusion':
			case '能量灌注': // Power Infusion in Chinese
				if (this.tag != -1) {
					if (this.tag === playerIndex || playerIndex == undefined) {
						name += ` (${t('gear.suffix.self')})`;
					} else {
						name += ` (${t('gear.suffix.raidFrom')} #${this.tag + 1})`;
					}
				} else {
					name += ` (${t('gear.suffix.raid')})`;
				}
				break;
			case 'Darkmoon Card: Crusade':
			case '暗月卡片：十字军': // Darkmoon Card: Crusade in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.melee')})`;
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.spell')})`;
				}
				break;
			case 'Frozen Blows':
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.physical')})`;
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.frost')})`;
				}
				break;
			case 'Scourge Strike':
			case '天灾打击': // Scourge Strike in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.physical')})`;
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.shadow')})`;
				}
				break;
			case 'Heart Strike':
			case '心脏打击': // Heart Strike in Chinese
				/*if (this.tag == 1) {
					name += ` (${t('gear.suffix.physical')})`;
				} else */if (this.tag == 2) {
					name += ` (${t('gear.suffix.offTarget')})`;
				}
				break;
			case 'Rune Strike':
			case '符文打击': // Rune Strike in Chinese
				if (this.tag == 0) {
					name += ` (${t('gear.suffix.queue')})`
				} else if (this.tag == 1) {
					name += ` (${t('gear.suffix.mainHand')})`;
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.offHand')})`;
				}
				break;
			case 'Frost Strike':
			case '冰霜打击': // Frost Strike in Chinese
			case 'Plague Strike':
			case '灵界打击': // Plague Strike in Chinese
			case 'Blood Strike':
			case '鲜血打击': // Blood Strike in Chinese
			case 'Death Strike':
			case '暗影打击': // Death Strike in Chinese
			case 'Obliterate':
			case '湮没': // Obliterate in Chinese
			case 'Blood-Caked Strike':
			case '血染之刃': // Blood-Caked Strike in Chinese
			case 'Lightning Speed':
			case '闪电速度': // Lightning Speed in Chinese
			case 'Windfury Weapon':
			case '风怒武器': // Windfury Weapon in Chinese
			case 'Berserk':
			case '狂暴': // Berserk in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.mainHand')})`;
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.offHand')})`;
				}
				break;
			case 'Battle Shout':
			case '战斗怒吼': // Battle Shout in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.snapshot')})`;
				}
				break;
			case 'Heroic Strike':
			case '英勇打击': // Heroic Strike in Chinese
			case 'Cleave':
			case '顺劈斩': // Cleave in Chinese
			case 'Maul':
			case '槌击': // Maul in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.queue')})`;
				}
				break;
			case 'Whirlwind':
			case '旋风斩': // Whirlwind in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.oh')})`;
				}
				break;
			case 'Seed of Corruption':
			case '腐蚀之种': // Seed of Corruption in Chinese
				if (this.tag == 0) {
					name += ` (${t('gear.suffix.dot')})`;
				} else if (this.tag == 1) {
					name += ` (${t('gear.suffix.explosion')})`;
				}
				break;
			case 'Thunderfury':
			case '雷霆之怒': // Thunderfury in Chinese
				if (this.tag == 1) {
					name += ` (${t('gear.suffix.st')})`;
				} else if (this.tag == 2) {
					name += ` (${t('gear.suffix.mt')})`;
				}
				break;
			default:
				if (this.tag) {
					name += ` (${t('gear.suffix.unknown')})`;
				}
				break;
		}

		const idString = this.toProtoString();
		const iconOverrideId = idOverrides[idString] || null;

		let iconUrl = ActionId.makeIconUrl(tooltipData['icon']);
		if (iconOverrideId) {
			const overrideTooltipData = await ActionId.getTooltipData(iconOverrideId);
			iconUrl = ActionId.makeIconUrl(overrideTooltipData['icon']);
		}

		return new ActionId(this.itemId, this.spellId, this.otherId, this.tag, baseName, name, iconUrl);
	}

	toString(): string {
		return this.toStringIgnoringTag() + (this.tag ? ('-' + this.tag) : '');
	}

	toStringIgnoringTag(): string {
		if (this.itemId) {
			return 'item-' + this.itemId;
		} else if (this.spellId) {
			return 'spell-' + this.spellId;
		} else if (this.otherId) {
			return 'other-' + this.otherId;
		} else {
			throw new Error('Empty action id!');
		}
	}

	toProto(): ActionIdProto {
		const protoId = ActionIdProto.create({
			tag: this.tag,
		});

		if (this.itemId) {
			protoId.rawId = {
				oneofKind: 'itemId',
				itemId: this.itemId,
			};
		} else if (this.spellId) {
			protoId.rawId = {
				oneofKind: 'spellId',
				spellId: this.spellId,
			};
		} else if (this.otherId) {
			protoId.rawId = {
				oneofKind: 'otherId',
				otherId: this.otherId,
			};
		}

		return protoId;
	}

	toProtoString(): string {
		return ActionIdProto.toJsonString(this.toProto());
	}

	withoutTag(): ActionId {
		return new ActionId(this.itemId, this.spellId, this.otherId, 0, this.baseName, this.baseName, this.iconUrl);
	}

	static fromEmpty(): ActionId {
		return new ActionId(0, 0, OtherAction.OtherActionNone, 0, '', '', '');
	}

	static fromItemId(itemId: number, tag?: number): ActionId {
		return new ActionId(itemId, 0, OtherAction.OtherActionNone, tag || 0, '', '', '');
	}

	static fromSpellId(spellId: number, tag?: number): ActionId {
		return new ActionId(0, spellId, OtherAction.OtherActionNone, tag || 0, '', '', '');
	}

	static fromOtherId(otherId: OtherAction, tag?: number): ActionId {
		return new ActionId(0, 0, otherId, tag || 0, '', '', '');
	}

	static fromPetName(petName: string): ActionId {
		return petNameToActionId[petName] || new ActionId(0, 0, OtherAction.OtherActionPet, 0, petName, petName, petNameToIcon[petName] || '');
	}

	static fromItem(item: Item): ActionId {
		return ActionId.fromItemId(item.id);
	}

	static fromProto(protoId: ActionIdProto): ActionId {
		if (protoId.rawId.oneofKind == 'spellId') {
			return ActionId.fromSpellId(protoId.rawId.spellId, protoId.tag);
		} else if (protoId.rawId.oneofKind == 'itemId') {
			return ActionId.fromItemId(protoId.rawId.itemId, protoId.tag);
		} else if (protoId.rawId.oneofKind == 'otherId') {
			return ActionId.fromOtherId(protoId.rawId.otherId, protoId.tag);
		} else {
			return ActionId.fromEmpty();
		}
	}

	private static readonly logRegex = /{((SpellID)|(ItemID)|(OtherID)): (\d+)(, Tag: (-?\d+))?}/;
	private static readonly logRegexGlobal = new RegExp(ActionId.logRegex, 'g');
	private static fromMatch(match: RegExpMatchArray): ActionId {
		const idType = match[1];
		const id = parseInt(match[5]);
		return new ActionId(
			idType == 'ItemID' ? id : 0,
			idType == 'SpellID' ? id : 0,
			idType == 'OtherID' ? id : 0,
			match[7] ? parseInt(match[7]) : 0,
			'', '', '');
	}
	static fromLogString(str: string): ActionId {
		const match = str.match(ActionId.logRegex);
		if (match) {
			return ActionId.fromMatch(match);
		} else {
			console.warn('Failed to parse action id from log: ' + str);
			return ActionId.fromEmpty();
		}
	}

	static async replaceAllInString(str: string): Promise<string> {
		const matches = [...str.matchAll(ActionId.logRegexGlobal)];

		const replaceData = await Promise.all(matches.map(async match => {
			const actionId = ActionId.fromMatch(match);
			const filledId = await actionId.fill();
			return {
				firstIndex: match.index || 0,
				len: match[0].length,
				actionId: filledId,
			};
		}));

		// Loop in reverse order so we can greedily apply the string replacements.
		for (let i = replaceData.length - 1; i >= 0; i--) {
			const data = replaceData[i];
			str = str.substring(0, data.firstIndex) + data.actionId.name + str.substring(data.firstIndex + data.len);
		}

		return str;
	}

	private static makeIconUrl(iconLabel: string): string {
		return `/wotlk/assets/icons/${iconLabel}.jpg`;
	}

	static async getTooltipData(actionId: ActionId): Promise<IconData> {
		if (actionId.itemId) {
			return await Database.getItemIconData(actionId.itemId);
		} else {
			return await Database.getSpellIconData(actionId.spellId);
		}
	}
}

// Some items/spells have weird icons, so use this to show a different icon instead.
const idOverrides: Record<string, ActionId> = {};
idOverrides[ActionId.fromSpellId(37212).toProtoString()] = ActionId.fromItemId(29035); // Improved Wrath of Air Totem
idOverrides[ActionId.fromSpellId(37223).toProtoString()] = ActionId.fromItemId(29040); // Improved Strength of Earth Totem
idOverrides[ActionId.fromSpellId(37447).toProtoString()] = ActionId.fromItemId(30720); // Serpent-Coil Braid
idOverrides[ActionId.fromSpellId(37443).toProtoString()] = ActionId.fromItemId(30196); // Robes of Tirisfal (4pc bonus)

export const defaultTargetIcon = '/wotlk/assets/icons/spell_shadow_metamorphosis.jpg';

const petNameToActionId: Record<string, ActionId> = {
	'Gnomish Flame Turret': ActionId.fromItemId(23841),
	'Mirror Image': ActionId.fromSpellId(55342),
	'Water Elemental': ActionId.fromSpellId(31687),
	"Greater Fire Elemental": ActionId.fromSpellId(2894),
	'Shadowfiend': ActionId.fromSpellId(34433),
	'Spirit Wolf 1': ActionId.fromSpellId(51533),
	'Spirit Wolf 2': ActionId.fromSpellId(51533),
	'Rune Weapon': ActionId.fromSpellId(49028),
	'Bloodworm': ActionId.fromSpellId(50452),
	'Gargoyle': ActionId.fromSpellId(49206),
	'Ghoul': ActionId.fromSpellId(46584),
	'Army of the Dead': ActionId.fromSpellId(42650),
	'Valkyr': ActionId.fromSpellId(71844),
};

// https://wowhead.com/wotlk/hunter-pets
const petNameToIcon: Record<string, string> = {
	'Bat': '/wotlk/assets/icons/ability_hunter_pet_bat.jpg',
	'Bear': '/wotlk/assets/icons/ability_hunter_pet_bear.jpg',
	'Bird of Prey': '/wotlk/assets/icons/ability_hunter_pet_owl.jpg',
	'Boar': '/wotlk/assets/icons/ability_hunter_pet_boar.jpg',
	'Carrion Bird': '/wotlk/assets/icons/ability_hunter_pet_vulture.jpg',
	'Cat': '/wotlk/assets/icons/ability_hunter_pet_cat.jpg',
	'Chimaera': '/wotlk/assets/icons/ability_hunter_pet_chimera.jpg',
	'Core Hound': '/wotlk/assets/icons/ability_hunter_pet_corehound.jpg',
	'Crab': '/wotlk/assets/icons/ability_hunter_pet_crab.jpg',
	'Crocolisk': '/wotlk/assets/icons/ability_hunter_pet_crocolisk.jpg',
	'Devilsaur': '/wotlk/assets/icons/ability_hunter_pet_devilsaur.jpg',
	'Dragonhawk': '/wotlk/assets/icons/ability_hunter_pet_dragonhawk.jpg',
	'Felguard': '/wotlk/assets/icons/spell_shadow_summonfelguard.jpg',
	'Felhunter': '/wotlk/assets/icons/spell_shadow_summonfelhunter.jpg',
	'Infernal': '/wotlk/assets/icons/spell_shadow_summoninfernal.jpg',
	'Gorilla': '/wotlk/assets/icons/ability_hunter_pet_gorilla.jpg',
	'Hyena': '/wotlk/assets/icons/ability_hunter_pet_hyena.jpg',
	'Imp': '/wotlk/assets/icons/spell_shadow_summonimp.jpg',
	'Mirror Image': '/wotlk/assets/icons/spell_magic_lesserinvisibilty.jpg',
	'Moth': '/wotlk/assets/icons/ability_hunter_pet_moth.jpg',
	'Nether Ray': '/wotlk/assets/icons/ability_hunter_pet_netherray.jpg',
	'Owl': '/wotlk/assets/icons/ability_hunter_pet_owl.jpg',
	'Raptor': '/wotlk/assets/icons/ability_hunter_pet_raptor.jpg',
	'Ravager': '/wotlk/assets/icons/ability_hunter_pet_ravager.jpg',
	'Rhino': '/wotlk/assets/icons/ability_hunter_pet_rhino.jpg',
	'Scorpid': '/wotlk/assets/icons/ability_hunter_pet_scorpid.jpg',
	'Serpent': '/wotlk/assets/icons/spell_nature_guardianward.jpg',
	'Silithid': '/wotlk/assets/icons/ability_hunter_pet_silithid.jpg',
	'Spider': '/wotlk/assets/icons/ability_hunter_pet_spider.jpg',
	'Spirit Beast': '/wotlk/assets/icons/ability_druid_primalprecision.jpg',
	'Spore Bat': '/wotlk/assets/icons/ability_hunter_pet_sporebat.jpg',
	'Succubus': '/wotlk/assets/icons/spell_shadow_summonsuccubus.jpg',
	'Tallstrider': '/wotlk/assets/icons/ability_hunter_pet_tallstrider.jpg',
	'Turtle': '/wotlk/assets/icons/ability_hunter_pet_turtle.jpg',
	'Warp Stalker': '/wotlk/assets/icons/ability_hunter_pet_warpstalker.jpg',
	'Wasp': '/wotlk/assets/icons/ability_hunter_pet_wasp.jpg',
	'Wind Serpent': '/wotlk/assets/icons/ability_hunter_pet_windserpent.jpg',
	'Wolf': '/wotlk/assets/icons/ability_hunter_pet_wolf.jpg',
	'Worm': '/wotlk/assets/icons/ability_hunter_pet_worm.jpg',
};

export function getPetIconFromName(name: string): string|ActionId|undefined {
	return petNameToActionId[name] || petNameToIcon[name];
}

export const resourceTypeToIcon: Record<ResourceType, string> = {
	[ResourceType.ResourceTypeNone]: '',
	[ResourceType.ResourceTypeHealth]: '/wotlk/assets/icons/inv_elemental_mote_life01.jpg',
	[ResourceType.ResourceTypeMana]: '/wotlk/assets/icons/inv_elemental_mote_mana.jpg',
	[ResourceType.ResourceTypeEnergy]: '/wotlk/assets/icons/spell_shadow_shadowworddominate.jpg',
	[ResourceType.ResourceTypeRage]: '/wotlk/assets/icons/spell_misc_emotionangry.jpg',
	[ResourceType.ResourceTypeComboPoints]: '/wotlk/assets/icons/inv_mace_2h_pvp410_c_01.jpg',
	[ResourceType.ResourceTypeFocus]: '/wotlk/assets/icons/ability_hunter_focusfire.jpg',
	[ResourceType.ResourceTypeRunicPower]: '/wotlk/assets/icons/inv_sword_62.jpg',
	[ResourceType.ResourceTypeBloodRune]: '/wotlk/assets/img/blood_rune.png',
	[ResourceType.ResourceTypeFrostRune]: '/wotlk/assets/img/frost_rune.png',
	[ResourceType.ResourceTypeUnholyRune]: '/wotlk/assets/img/unholy_rune.png',
	[ResourceType.ResourceTypeDeathRune]: '/wotlk/assets/img/death_rune.png',
};
